import moment from "moment";
import router from "../../router";
import { cacheTime, logoutTime } from "../../utils/constant";
import { getExpired, getIssuedAt, isExpired } from "../../utils/jwt";
import request from "../../utils/request";
import websocket from "../../utils/websocket";

const baseUrl = "/api/auth";
let intervalRefreshToken = null;
let intervalLogout = null;

export default {
    namespaced: true,
    state: {
        token: null,
        refreshToken: null,
        rememberMe: false,
        tokenDatetime: null,
        info: null,
        cacheDate: {
            info: null
        }
    },
    getters: {
        isAuth(state) {
            return state.token != null;
        },
        getToken(state) {
            return state.token;
        }
    },
    actions: {
        loadToken(context) {
            const token = localStorage.getItem("token");
            const refreshToken = localStorage.getItem("refreshToken");
            const rememberMe = localStorage.getItem("rememberMe");
            const tokenDatetime = localStorage.getItem("tokenDatetime");
            const if1 = !rememberMe && (!token || isExpired(token));
            const if2 = rememberMe && (!refreshToken || isExpired(refreshToken));
            if (if1 || if2) {
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                localStorage.removeItem("rememberMe");
                localStorage.removeItem("tokenDatetime");
            } else {
                context.commit("setToken", token);
                context.commit("setRefreshToken", refreshToken);
                context.commit("setRememberMe", rememberMe);
                context.commit("setTokenDatetime", tokenDatetime);
                context.dispatch("refreshToken").then(() => context.dispatch("setIntervalRefreshToken"));
            }
        },
        login(context, { data = {}, config = {} }) {
            return request
                .post(baseUrl, data, { ...config })
                .then(data => {
                    context.commit("setToken", data.token);
                    context.commit("setRefreshToken", data.refreshToken);
                    context.commit("setRememberMe", false);
                    context.commit("setTokenDatetime", new Date().getTime());
                    context.dispatch("setIntervalRefreshToken");
                    if (websocket.isConnected) {
                        websocket.disconnect();
                        websocket.connect();
                    }
                    return data;
                })
                .catch(error => {
                    context.dispatch("logout");
                    return Promise.reject(error);
                });
        },
        logout(context) {
            Promise.resolve()
                .then(() => {
                    const username = context.state.info?.user.username;
                    if (username) {
                        return request.post(`${baseUrl}/logout`, { username }).catch(() => {});
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    context.commit("setToken", null);
                    context.commit("setRefreshToken", null);
                    context.commit("setRememberMe", null);
                    context.commit("setTokenDatetime", null);
                    context.commit("setInfo", null);

                    localStorage.removeItem("token");
                    localStorage.removeItem("refreshToken");
                    localStorage.removeItem("rememberMe");
                    context.dispatch("clearIntervalRefreshToken");
                    if (router.currentRoute.value.name !== "login") {
                        if (websocket.isConnected) {
                            websocket.clearAll();
                            websocket.disconnect();
                        }
                        window.location.href = process.env.VUE_APP_PUBLIC_PATH;
                    }
                    return Promise.resolve();
                });
        },
        refreshToken(context) {
            const token = context.state.rememberMe ? context.state.refreshToken : context.state.token;
            return request
                .post(`${baseUrl}/token`, { token: token })
                .then(data => {
                    context.commit("setToken", data.token);
                    context.commit("setRefreshToken", data.refreshToken);
                    context.commit("setTokenDatetime", new Date().getTime());
                    /*
                    console.log('Refresh token: ', {
                        token: data.token,
                        issuedAt: new Date(getIssuedAt(context.state.token)),
                        expired: new Date(getExpired(context.state.token)),
                    })
                    */
                    return data;
                })
                .catch(error => {
                    context.dispatch("logout");
                    return Promise.reject(error);
                });
        },
        setIntervalRefreshToken(context) {
            const token = context.state.token;
            const tokenDatetime = context.state.tokenDatetime;
            const issuedAt = getIssuedAt(token);
            const expired = getExpired(token);
            const now = new Date().getTime();
            const schedule = ((expired - issuedAt) * 50.0) / 100.0 - (now - tokenDatetime);
            context.dispatch("clearIntervalRefreshToken");
            intervalRefreshToken = setTimeout(async () => {
                await context.dispatch("refreshToken");
                context.dispatch("setIntervalRefreshToken");
            }, schedule);
            context.dispatch("setIntervalLogout");
        },
        clearIntervalRefreshToken() {
            if (intervalRefreshToken) {
                clearTimeout(intervalRefreshToken);
                intervalRefreshToken = null;
            }
        },
        setIntervalLogout(context) {
            context.dispatch("clearIntervalLogout");
            intervalLogout = setTimeout(() => {
                context.dispatch("logout");
            }, logoutTime);
        },
        clearIntervalLogout() {
            if (intervalLogout) {
                clearTimeout(intervalLogout);
                intervalLogout = null;
            }
        },
        getInfo(context, { config = {}, options = {} }) {
            options = {
                cache: true,
                force: false,
                ...options
            };
            if (context.state.cacheDate.info && context.state.cacheDate.info.isBefore(moment())) {
                options.force = true;
            }
            const info = context.state.info;
            if (info && !options.force) {
                return new Promise(reslove => reslove(info));
            }
            const token = context.state.token;
            return request
                .post(`${baseUrl}/info`, { token: token }, config)
                .then(info => {
                    if (options.cache) {
                        context.commit("setInfo", info);
                    }
                    return info;
                })
                .catch(error => {
                    context.dispatch("logout");
                    return Promise.reject(error);
                });
        },
        forgotPassword(context, { data, config = {} }) {
            return request.post(`${baseUrl}/forgot-password`, data, { ...config });
        },
        getForgotPasswordByKey(context, { key, config = {} }) {
            return request.get(`${baseUrl}/forgot-password-by-key`, { params: { key: key }, ...config });
        },
        activeForgotPassword(context, { data, config = {} }) {
            return request.post(`${baseUrl}/active-forgot-password`, data, { ...config });
        }
    },
    mutations: {
        setToken(state, token) {
            state.token = token;
            if (token) {
                localStorage.setItem("token", token);
            } else {
                localStorage.removeItem("token");
            }
        },
        setRefreshToken(state, refreshToken) {
            state.refreshToken = refreshToken;
            if (refreshToken) {
                localStorage.setItem("refreshToken", refreshToken);
            } else {
                localStorage.removeItem("refreshToken");
            }
        },
        setRememberMe(state, rememberMe) {
            state.rememberMe = rememberMe;
            if (rememberMe) {
                localStorage.setItem("rememberMe", rememberMe);
            } else {
                localStorage.removeItem("rememberMe");
            }
        },
        setTokenDatetime(state, tokenDatetime) {
            state.tokenDatetime = tokenDatetime;
            if (tokenDatetime) {
                localStorage.setItem("tokenDatetime", tokenDatetime);
            } else {
                localStorage.removeItem("tokenDatetime");
            }
        },
        setInfo(state, info) {
            state.info = info;
            state.cacheDate.info = moment().add(cacheTime, "ms");
        }
    }
};
